<script>
// Casca da sidebar de contexto: largura aberta/recolhida, faixa de accent
// opcional (sinaliza contexto cliente), botão circular de recolher e três
// regiões via slot (brand, default, footer).
import {
  SIDEBAR_WIDTH_OPEN,
  SIDEBAR_WIDTH_SMALL,
} from '@modules/navegacao-v2/constants/layout'
import { navSurfaceVars } from '@modules/navegacao-v2/utils/nav-surface'

const TOGGLE_ICON_SIZE = 14

export default {
  name: 'SidebarShell',
  props: {
    collapsed: { type: Boolean, default: false },
    accentTop: { type: Boolean, default: false },
    // Em tela pequena a sidebar vira drawer off-canvas (controlado por `open`).
    mobile: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
    // Cor da faixa de contexto (accentTop): primária para cliente, cinza p/ lead.
    accentColor: { type: String, default: 'var(--primary)' },
  },
  data() {
    return { TOGGLE_ICON_SIZE }
  },
  computed: {
    // As custom properties de contraste nascem aqui e valem para toda a árvore da
    // sidebar (inclusive o flyout, que é filho no DOM mesmo sendo position: fixed).
    asideStyle() {
      return { ...this.sizeStyle, ...navSurfaceVars(this.$Color()['bg-navbar']) }
    },
    sizeStyle() {
      if (this.mobile) {
        const w = `${SIDEBAR_WIDTH_OPEN}px`
        const transform = this.open ? 'translateX(0)' : 'translateX(-100%)'
        return { width: w, minWidth: w, transform }
      }
      const width = `${this.collapsed ? SIDEBAR_WIDTH_SMALL : SIDEBAR_WIDTH_OPEN}px`
      return { width, minWidth: width }
    },
    toggleIcon() {
      return this.collapsed ? 'mdi-chevron-right' : 'mdi-chevron-left'
    },
    toggleTitle() {
      return this.collapsed ? 'Expandir menu' : 'Recolher menu'
    },
  },
}
</script>

<template>
  <aside
    class="sidebar-shell"
    :class="{ 'sidebar-shell--mobile': mobile }"
    :style="asideStyle"
  >
    <div v-if="accentTop" class="sidebar-shell__accent" :style="{ background: accentColor }" />

    <button
      v-if="!mobile"
      class="sidebar-shell__toggle"
      type="button"
      :title="toggleTitle"
      @click="$emit('toggle')"
    >
      <icon-svg :name="toggleIcon" :size="TOGGLE_ICON_SIZE" color="#030309" />
    </button>

    <div class="sidebar-shell__brand">
      <slot name="brand" />
    </div>

    <div class="sidebar-shell__body">
      <slot />
    </div>

    <div v-if="!collapsed && $slots.footer" class="sidebar-shell__footer">
      <slot name="footer" />
    </div>
  </aside>
</template>

<style lang="sass" scoped>
.sidebar-shell
  position: fixed
  left: 0
  top: 0
  height: 100vh
  z-index: 60
  display: flex
  flex-direction: column
  box-sizing: border-box
  background: var(--bg-navbar, #030309)
  color: var(--nav-fg, #fff)
  border-right: 1px solid rgba(var(--nav-fg-rgb, 255, 255, 255), 0.06)
  transition: width 0.2s cubic-bezier(0.16, 0.84, 0.44, 1), transform 0.25s cubic-bezier(0.16, 0.84, 0.44, 1)

  &--mobile
    z-index: 70
    box-shadow: 0 24px 64px rgba(3, 3, 9, 0.5)

  &__accent
    height: 3px
    flex-shrink: 0

  &__toggle
    position: absolute
    top: 78px
    right: -13px
    z-index: 20
    width: 26px
    height: 26px
    border-radius: 999px
    // Pastilha clara com ícone escuro em qualquer tema: ela vive sobre a divisa
    // sidebar/conteúdo, e o anel cinza é o que a separa de um fundo branco.
    border: 1px solid #e4e4e4
    background: rgba(255, 255, 255, 0.9)
    box-shadow: 0 2px 8px rgba(3, 3, 9, 0.16)
    cursor: pointer
    display: flex
    align-items: center
    justify-content: center

  &__brand
    flex-shrink: 0

  &__body
    flex: 1
    overflow-y: auto
    overflow-x: hidden
    padding: 6px 12px 12px
    // Scrollbar no tom do texto de contraste (era clara/padrão): trilho
    // transparente (mostra o fundo) + polegar translúcido.
    scrollbar-width: thin
    scrollbar-color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.2) transparent
    &::-webkit-scrollbar
      width: 6px
    &::-webkit-scrollbar-track
      background: transparent
    &::-webkit-scrollbar-thumb
      background-color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.2)
      border-radius: 999px
    &::-webkit-scrollbar-thumb:hover
      background-color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.35)

  &__footer
    padding: 12px 16px
    border-top: 1px solid rgba(var(--nav-fg-rgb, 255, 255, 255), 0.08)
</style>
